import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ActionsPanel from './components/ActionsPanel';
import Header from './components/Header';
import NothingFoundCard from '../../../../components/generics/Cards/NothingFoundCard';
import { Table, TableCell, TableHead, TableRow, TableSortCell } from '../../../../components/common/ResponsiveTable';
import { useTrans } from '../../../../services/i18n';
import { Grid, Button, TableContainer, List, ListItem, ListItemText } from '@material-ui/core';
import styles from './styles.module.css';
import connect from '../connect';
import { emptyArray } from '../../../../utils/arrayUtils';
import { ReactComponent as VendorsIcon } from '../icons/ico-vendor-management-empty.svg';
import { ReactComponent as NoArchivedIcon } from '../icons/ico-no-archived.svg';
import AddVendorManuallyForm from '../components/AddVendorManuallyForm';
import AddVendorForm from '../components/AddVendorForm';
import AddVendorTagsForm from '../components/AddVendorTagsForm';
import AddVendorsCsvForm from '../components/AddVendorsCsvForm';
import { CheckBox } from '../../../../components/common/Inputs/CheckBox';
import { formatDatePretty } from '../../../../utils/dateUtils';
import Loader from '../../../..//components/common/Loader';
import { makeStyles } from '@material-ui/core/styles';
import BorderColorOutlinedIcon from '@material-ui/icons/BorderColorOutlined';
import { ModalDialog } from '../../../../components/common/Dialogs';
import useBooleanHandlers from '../../../../hooks/useBooleanHandlers';
import { SpacingContainer } from '../../../../components/common/Containers/Base';
import SectionScrollable from '../../../../components/common/Containers/SectionScrollable';
import Typography from '../../../../components/common/Typography';
import { Divider } from '../../../../components/common/Divider';
import CodeDescription from './components/CodeDescription';
import TagsCell from './components/TagsCell';
import CodesCell from './components/CodesCell';
import { ROUTE_VENDORS_MANAGEMENT_DETAILS, ROUTE_VENDORS_DETAILS } from '../../../../constants/routes';
import { RouterLink } from '../../../../components/common/Buttons';
import { CSVLink } from 'react-csv';
import dayjs from 'dayjs';
import {
  getSbaCertificationDisplayName,
  getSbaCertificationExpirationDate,
  isSbaCertificationExpiringSoon,
} from './helpers';
import CrmOrganizationIndicator from './components/CrmOrganizationIndicator';
import { getVendorColumnCellProps, getVendorSelectionCellProps } from './stickyCellProps';
import {
  createFiltersWithVendorOrder,
  getVendorOrderLabel,
  getVendorOrderState,
  getVendorRowKey,
  getVisibleVendorColumns,
  isVendorColumnSortable,
} from './stickyColumnHelpers';

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
  linkStyle: {
    color: '#2445A7',
    cursor: 'pointer',
  },
  nameWithCrm: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    maxWidth: '100%',
  },
});

const modalNames = {
  naics: 'NAICS Codes',
  setAsides: 'Self Certified Set-Aside Codes',
  psc: 'PSC Codes',
  tags: 'Tags',
  sbaDetails: 'SBA Certification Codes',
};

const getVendorRedirectPath = (item) => {
  if (!item) return '';
  if (item.status === 'Unregistered') {
    return ROUTE_VENDORS_MANAGEMENT_DETAILS.path.replace(':id', item.id);
  }
  if (item.status === 'Registered') {
    return ROUTE_VENDORS_DETAILS.path.replace(':id', item.documentId);
  }
  return '';
};

const getVendorStatusLabel = (item) => {
  const isExpired = item.expirationDate && dayjs(item.expirationDate).isBefore(dayjs(), 'day');
  if (item.archived) return 'Archived';
  if (isExpired) return 'Expired';
  return item.status;
};

export const VendorsManagementList = ({
  currentView,
  items,
  count,
  getList,
  itemCheck,
  filters,
  openAddVendorModal,
  openAddVendor,
  openAddVendorManuallyModal = false,
  openAddVendorManually,
  closeDrawer,
  openAddTagsModal,
  openAddTags,
  openImportVendorsCsvModal,
  openImportVendors,
  tableColumns,
  getVendorTags,
  pending,
  filterDefault,
  textSearch,
  changeFilters,
}) => {
  const { trans } = useTrans();
  const classes = useStyles();
  const [open, onOpen, onClose] = useBooleanHandlers();

  const [modalName, setModalName] = useState([]);
  const [codes, setCodes] = useState([]);
  const activeSearchText = (filters?.text || '').trim();
  const isSearchActive = !!activeSearchText;
  const visibleColumns = useMemo(() => getVisibleVendorColumns(tableColumns), [tableColumns]);
  const { orderBy, orderDir } = useMemo(() => getVendorOrderState(filters), [filters]);
  const selectionHeaderCellProps = getVendorSelectionCellProps({ isHeader: true });
  const selectionBodyCellProps = getVendorSelectionCellProps();

  const onChangeOrder = (nextOrder) => {
    changeFilters(createFiltersWithVendorOrder(filters, nextOrder));
  };

  const showCodes = (column, codesList) => {
    setModalName(column);
    setCodes(codesList);
    onOpen();
  };

  useEffect(() => {
    getList({
      ...filters,
      type: filterDefault,
    });
  }, [filters, filterDefault, getList]);

  const getStatusStyleCell = (status, archived) => {
    if (archived) return styles.archived;
    if (status === 'Registered') return styles.registered;
    if (status === 'Unregistered' || status === 'Expired') return styles.unregistered;
    return undefined;
  };

  const onItemCheck = (ev, item) => {
    itemCheck({ id: item.id, checked: ev.target.checked });
  };

  const getTableCell = (column, item) => {
    const { className: stickyClassName, style: stickyStyle } = getVendorColumnCellProps(visibleColumns, column);

    if (column.value === 'name') {
      return (
        <TableCell
          key={column.value}
          className={[classes.linkStyle, stickyClassName].filter(Boolean).join(' ')}
          style={stickyStyle}
        >
          <div className={classes.nameWithCrm}>
            <Typography color="primary" component={RouterLink} to={getVendorRedirectPath(item)} noWrap={false}>
              {item[column.value]}
            </Typography>
            <CrmOrganizationIndicator crmCompanyId={item.crmCompanyId} />
          </div>
        </TableCell>
      );
    }

    if (column.value === 'status') {
      const status = getVendorStatusLabel(item);
      return (
        <TableCell key={column.value} className={getStatusStyleCell(status, item.archived)}>
          <p>{status}</p>
        </TableCell>
      );
    }

    if (column.value === 'expirationDate') {
      const expiration = item[column.value];
      const isExpiringSoon = dayjs(expiration).diff(dayjs(), 'day') < 90;
      return (
        <TableCell key={column.value}>
          <Typography style={{ color: isExpiringSoon ? 'red' : undefined }}>{formatDatePretty(expiration)}</Typography>
        </TableCell>
      );
    }

    if (column.value === 'bussinessStartDate') {
      return <TableCell key={column.value}>{formatDatePretty(item[column.value])}</TableCell>;
    }

    if (column.value === 'tags') {
      return (
        <TableCell key={column.value}>
          <TagsCell tags={item.tags} onOpen={(tagsList) => showCodes('tags', tagsList)} />
        </TableCell>
      );
    }

    if (['naics', 'setAsides', 'psc', 'sbaDetails'].includes(column.value)) {
      return (
        <TableCell key={column.value}>
          <CodesCell columnValue={column.value} values={item[column.value]} onOpen={showCodes} />
        </TableCell>
      );
    }

    return <TableCell key={column.value}>{item[column.value]}</TableCell>;
  };

  useEffect(() => {
    getVendorTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getList]);

  const clearSearch = () => {
    const updated = {
      ...filters,
      text: '',
      pagination: { ...filters.pagination, page: 1 },
    };
    changeFilters(updated);
  };

  const exportHeaders = [];
  const exportData = [];
  if (items && items.length > 0) {
    exportHeaders.push(
      { label: 'Vendor Name', key: 'vendor-name' },
      { label: 'Status', key: 'status' },
      { label: 'Expiration Date', key: 'expiration-date' },
      { label: 'Cage Code', key: 'cage-code' },
      { label: 'DUNS Number', key: 'duns-number' },
      { label: 'UEI Code', key: 'uei-code' },
      { label: 'Business Start Date', key: 'business-start-date' },
      { label: 'Self Certifications', key: 'SetAsides' },
      { label: 'SBA Certifications', key: 'SbaDetails' },
      { label: 'NAICS', key: 'Naics' },
      { label: 'PSC', key: 'Psc' },
      { label: 'Tags', key: 'Tags' }
    );
    items.forEach((item) => {
      exportData.push({
        'vendor-name': item.name,
        status: item.status,
        'expiration-date': item.expirationDate,
        'cage-code': item.cageCode,
        'duns-number': item.dunsCode,
        'uei-code': item.ueiCode,
        'business-start-date': item.bussinessStartDate,
        SetAsides: item.setAsides ? item.setAsides.map((setAside) => setAside.code).join(', ') : '',
        SbaDetails: item.sbaCertifications ? item.sbaCertifications.map((sba) => sba.name).join(', ') : '',
        Naics: item.naics ? item.naics.map((naic) => naic.code).join(', ') : '',
        Psc: item.psc ? item.psc.map((psc) => psc.code).join(', ') : '',
        Tags: item.tags ? item.tags.join(', ') : '',
      });
    });
  }

  return (
    <>
      <div style={{ padding: '10px' }}>
        <Loader open={pending} />
        <AddVendorManuallyForm open={openAddVendorManuallyModal} onClose={() => openAddVendorManually(false)} />
        <AddVendorForm open={openAddVendorModal} onClose={() => openAddVendor(false)} />
        <AddVendorTagsForm open={openAddTagsModal} onClose={() => openAddTags(false)} />
        <AddVendorsCsvForm open={openImportVendorsCsvModal} onClose={() => openImportVendors(false)} />
        <Header closeDrawer={closeDrawer} />

        <Grid container spacing={2} justifyContent="flex-start" style={{ margin: '16px 0 0 0' }}>
          <Grid item>
            <Button variant="contained" color="primary" onClick={() => openAddVendor(true)}>
              {trans('add-vendor')}
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" color="primary" onClick={() => openAddVendorManually(true)}>
              {trans('add-vendor-manually2')}
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" color="primary" onClick={() => openImportVendors(true)}>
              {trans('import-csv')}
            </Button>
          </Grid>
          <Grid item>
            <CSVLink
              data={exportData}
              headers={exportHeaders}
              filename={'vendors_data.csv'}
              style={{ textDecoration: 'none' }}
            >
              <Button variant="contained" color="primary">
                {trans('export-vendors')}
              </Button>
            </CSVLink>
          </Grid>
        </Grid>
        <ActionsPanel />
        {isSearchActive && (
          <Grid container alignItems="center" style={{ margin: '12px 0' }} spacing={1}>
            <Grid item>
              <Typography variant="body2" color="hint">
                Showing results for: "{activeSearchText}"
              </Typography>
            </Grid>
            <Grid item>
              <Button size="small" color="primary" onClick={clearSearch}>
                Reset search
              </Button>
            </Grid>
          </Grid>
        )}
        {!emptyArray(items) ? (
          <TableContainer className={styles.tableContainer}>
            <Table
              className={`${classes.table} ${styles.tablePinned}`}
              size="small"
              stickyHeader
              head={
                <TableHead>
                  <TableCell
                    className={[styles.tableHeadBg, selectionHeaderCellProps.className].join(' ')}
                    style={selectionHeaderCellProps.style}
                    noWrap
                  />
                  {visibleColumns.map((column) => {
                    const { className: stickyClassName, style } = getVendorColumnCellProps(visibleColumns, column, {
                      isHeader: true,
                    });
                    const cellClassName = [styles.tableHeadBg, styles.sortableHeadCell, stickyClassName]
                      .filter(Boolean)
                      .join(' ');
                    const orderLabel = getVendorOrderLabel(column);

                    if (!isVendorColumnSortable(column)) {
                      return (
                        <TableCell className={cellClassName} style={style} key={column.value} noWrap>
                          {trans(column.name)}
                        </TableCell>
                      );
                    }

                    return (
                      <TableSortCell
                        className={cellClassName}
                        style={style}
                        key={column.value}
                        noWrap
                        orderLabel={orderLabel}
                        orderBy={orderBy}
                        orderDir={orderDir}
                        onChange={onChangeOrder}
                      >
                        {trans(column.name)}
                      </TableSortCell>
                    );
                  })}
                </TableHead>
              }
            >
              {items.map(
                (item) =>
                  item && (
                    <TableRow
                      key={getVendorRowKey(item)}
                      hover
                      className={[styles.tableRow, item.checked ? styles.selectedRow : ''].filter(Boolean).join(' ')}
                    >
                      <TableCell className={selectionBodyCellProps.className} style={selectionBodyCellProps.style}>
                        <CheckBox
                          color="secondary"
                          onChange={(ev) => onItemCheck(ev, item)}
                          checked={item.checked}
                          md={{ '& .MuiSvgIcon-root': { fontSize: 50 } }}
                        />
                      </TableCell>
                      {visibleColumns.map((column) => getTableCell(column, item))}
                    </TableRow>
                  )
              )}
            </Table>
          </TableContainer>
        ) : currentView === 'vendors' && !pending ? (
          <NothingFoundCard
            title={isSearchActive ? `No vendors found for "${activeSearchText}"` : trans('you-add-no-vendors-yet')}
            description={
              isSearchActive ? 'Try a different search or reset to view all vendors.' : trans('all-manually-added')
            }
            size="large"
            Icon={<VendorsIcon />}
          >
            <br />
            <br />
            {isSearchActive ? (
              <Button color="primary" onClick={clearSearch}>
                Reset search
              </Button>
            ) : (
              <Button color="primary" onClick={() => openAddVendor(true)}>
                {trans('search-vendor')}
              </Button>
            )}
          </NothingFoundCard>
        ) : currentView === 'vendorsArchived' && !pending ? (
          <NothingFoundCard
            title={trans('your-archive-is-empty')}
            description={trans('all-archived-vendors')}
            size="large"
            Icon={<NoArchivedIcon />}
          ></NothingFoundCard>
        ) : (
          !pending && (
            <NothingFoundCard
              title={trans('your-draft-is-empty')}
              description={trans('all-draft-vendors')}
              size="large"
              Icon={<BorderColorOutlinedIcon style={{ width: '64px', height: '64px' }} />}
            ></NothingFoundCard>
          )
        )}
      </div>
      <ModalDialog open={open} onClose={onClose} disableContentSpacing title={modalNames[modalName]}>
        <SectionScrollable dividedFixed>
          <SpacingContainer px={4} py={1}>
            <List>
              {codes.map((item, index) => {
                const sbaExpirationDate = modalName === 'sbaDetails' ? getSbaCertificationExpirationDate(item) : null;
                const showSbaExpirationDate = modalName === 'sbaDetails' && isSbaCertificationExpiringSoon(item);

                return (
                  <React.Fragment key={item.code || item.name || item}>
                    <ListItem disableGutters>
                      <ListItemText>
                        <Typography fontFamily="lexend" color="primary" variant="body1" weight="bold">
                          {modalName === 'sbaDetails' ? getSbaCertificationDisplayName(item) : item.code || item}
                          {showSbaExpirationDate && (
                            <span style={{ color: 'red', marginLeft: 8 }}>
                              Expires {formatDatePretty(sbaExpirationDate)}
                            </span>
                          )}
                        </Typography>
                        {item.description && (
                          <Grid container>
                            <Grid item>
                              <CodeDescription item={item} />
                            </Grid>
                          </Grid>
                        )}
                      </ListItemText>
                    </ListItem>

                    {index + 1 < codes.length && <Divider light spacing={0} />}
                  </React.Fragment>
                );
              })}
            </List>
          </SpacingContainer>
        </SectionScrollable>
      </ModalDialog>
    </>
  );
};

const tableColumnShape = PropTypes.shape({
  name: PropTypes.string,
  value: PropTypes.string,
  active: PropTypes.bool,
});

VendorsManagementList.propTypes = {
  currentView: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object),
  count: PropTypes.number,
  getList: PropTypes.func,
  itemCheck: PropTypes.func,
  filters: PropTypes.object,
  openAddVendorModal: PropTypes.bool,
  openAddVendor: PropTypes.func,
  openAddVendorManuallyModal: PropTypes.bool,
  openAddVendorManually: PropTypes.func,
  closeDrawer: PropTypes.func,
  openAddTagsModal: PropTypes.bool,
  openAddTags: PropTypes.func,
  openImportVendorsCsvModal: PropTypes.bool,
  openImportVendors: PropTypes.func,
  tableColumns: PropTypes.arrayOf(tableColumnShape),
  getVendorTags: PropTypes.func,
  pending: PropTypes.bool,
  filterDefault: PropTypes.string,
  textSearch: PropTypes.string,
  changeFilters: PropTypes.func,
};

VendorsManagementList.defaultProps = {
  items: [],
  tableColumns: [],
};

export default connect(VendorsManagementList);
